/* eslint-disable react/jsx-max-depth, react/jsx-no-bind */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useTheme } from 'styled-components'
import {
  AIFilled,
  Close
} from '@dsny/dsny-component-library/icons'
import { MaterialIcon } from '@dsny/dsny-component-library'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { StyledModal } from 'components/Modals/Modals.styles'
import { ImageWithFallback } from 'components'
import { albumCovertArtPlaceholder } from 'images'
import { DATE_FORMAT, formatDateWithTimeZoneFromSeconds } from 'utils/dateHelpers'
import { trackEvent } from 'utils/segmentService'
import ContactModal from 'containers/ReleaseInformation/components/Modals/ContactModal/ContactModal'
import { thunks as ReleaseThunks } from 'reducers/Releases'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import QuickViewErrorState from './QuickViewErrorState'
import useQuickViewModalActions from './useQuickViewModalActions'
import QuickViewReleaseMenu from './QuickViewReleaseMenu'
import QuickViewTrackListSkeleton from './QuickViewTrackListSkeleton'
import QuickViewTrackRow from './QuickViewTrackRow'
import {
  QUICK_VIEW_VISIBLE_TRACK_ROWS,
  QuickViewModalCard,
  QuickViewCloseButton,
  QuickViewHeader,
  QuickViewCoverArt,
  QuickViewContent,
  QuickViewPillsRow,
  QuickViewPill,
  QuickViewTitle,
  QuickViewMeta,
  QuickViewActionsRow,
  QuickViewPrimaryButton,
  QuickViewSecondaryButton,
  QuickViewDivider,
  QuickViewTrackList
} from './QuickViewModal.styles'

const buildMetaItems = (release) => {
  if (!release) {
    return []
  }

  const items = []

  if (release?.available) {
    items.push(
      `Available date: ${format(new Date(release.available), DATE_FORMAT)}`
    )
  }

  if (release?.impactdate) {
    items.push(
      `Impact date: ${formatDateWithTimeZoneFromSeconds(release.impactdate)}`
    )
  }

  if (release?.genres?.length) {
    items.push(release.genres.join(' / '))
  }

  const labelNames = release?.labels
    ?.map((label) => label?.name)
    .filter(Boolean)
    .join(', ')

  if (labelNames) {
    items.push(labelNames)
  } else if (release?.publisher) {
    items.push(release.publisher)
  }

  return items
}

const hasLoadedReleaseDetail = (release) => Array.isArray(release?.songs)

const getEstimatedTrackCount = (release) => {
  if (Array.isArray(release?.songs) && release.songs.length > 0) {
    return release.songs.length
  }

  if (Array.isArray(release?.tracks) && release.tracks.length > 0) {
    return release.tracks.length
  }

  const normalizedTrackCount = Number(
    release?.trackcount ?? release?.tracks_count ?? release?.track_count
  )

  if (Number.isFinite(normalizedTrackCount) && normalizedTrackCount > 0) {
    return normalizedTrackCount
  }

  return QUICK_VIEW_VISIBLE_TRACK_ROWS
}

export default function QuickViewModal({ release, onClickCloseButton }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2

  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [releaseDetail, setReleaseDetail] = useState(
    hasLoadedReleaseDetail(release) ? release : null
  )
  const [detailStatus, setDetailStatus] = useState(
    hasLoadedReleaseDetail(release) ? 'success' : 'loading'
  )
  const { playlistAbstracts } = useSelector((state) => state.playList)
  const requestRef = useRef(0)
  const activeRelease = releaseDetail || release
  const metaItems = useMemo(() => buildMetaItems(activeRelease), [activeRelease])
  const estimatedTrackRowCount = useMemo(
    () =>
      Math.min(
        getEstimatedTrackCount(release),
        QUICK_VIEW_VISIBLE_TRACK_ROWS
      ),
    [release]
  )
  const {
    canShareRelease,
    didTapPlay,
    didTapPlayNext,
    didTapPlayLater,
    handleDownloadRelease,
    handleDownloadSong,
    handlePlayRelease,
    handlePlaySong,
    handleReleasePlayNext,
    handleReleasePlayLater,
    handleTrackPlayNext,
    handleTrackPlayLater,
    handleFlagRelease,
    handleArchiveRelease,
    handleAddReleaseToPlaylist,
    handleAddTrackToPlaylist,
    createNewReleasePlaylist,
    createNewTrackPlaylist,
    openShareReleaseModal,
    openContactModal
  } = useQuickViewModalActions({
    dispatch,
    release: activeRelease,
    setIsContactModalOpen
  })

  const fetchReleaseDetail = useCallback(() => {
    if (!release?.releaseid) {
      return Promise.resolve()
    }

    const requestId = requestRef.current + 1
    requestRef.current = requestId
    setDetailStatus('loading')

    return Promise.resolve(
      dispatch(
        ReleaseThunks.getDetail(String(release.releaseid), (detailRelease) => {
          if (requestRef.current !== requestId) {
            return
          }

          setReleaseDetail(detailRelease)
          setDetailStatus('success')
        })
      )
    ).catch(() => {
      if (requestRef.current !== requestId) {
        return
      }

      setDetailStatus('error')
      setReleaseDetail(null)
    })
  }, [dispatch, release])

  useEffect(() => {
    setIsContactModalOpen(false)

    if (hasLoadedReleaseDetail(release)) {
      requestRef.current += 1
      setReleaseDetail(release)
      setDetailStatus('success')
      return undefined
    }

    setReleaseDetail(null)
    fetchReleaseDetail()

    return () => {
      requestRef.current += 1
    }
  }, [fetchReleaseDetail, release])

  const handleClose = () => {
    if (onClickCloseButton) {
      onClickCloseButton()
    }
  }

  const handleViewRelease = () => {
    trackEvent('Click', {
      source: 'release_modal',
      type: 'info',
      release_id: String(activeRelease?.releaseid),
      artist: activeRelease?.artist || '',
      title: activeRelease?.title || ''
    })
    handleClose()
    navigate(`/releases/releaseInfo/${activeRelease.releaseid}`)
  }

  const releaseTitle = [activeRelease?.title, activeRelease?.artist]
    .filter(Boolean)
    .join(' - ')
  const tracks = activeRelease?.songs || []
  const isDetailLoading = detailStatus === 'loading'
  const hasDetailError = detailStatus === 'error'
  const isReleaseMenuDisabled = detailStatus !== 'success'

  return (
    <>
      <StyledModal
        isOpen
        $isMobile={isMobile}
        analyticsModalType="release_modal"
        trackEvent={trackEvent}
        containerStyles={{
          padding: isMobile ? '20px 16px 16px' : '32px',
          width: isMobile ? 'calc(100vw - 16px)' : 'min(960px, calc(100vw - 48px))',
          maxWidth: '960px'
        }}
        onClose={handleClose}>
        <QuickViewModalCard
          data-responsive-breakpoints={responsiveBreakpointsVersion}>
          <QuickViewCloseButton
            aria-label={t('general.cancel')}
            type="button"
            onClick={handleClose}>
            <Close width={24} color={theme.colors.neutralW90} />
          </QuickViewCloseButton>

          <QuickViewHeader
            $responsiveBreakpointsV2={responsiveBreakpointsV2}>
            <QuickViewCoverArt
              $responsiveBreakpointsV2={responsiveBreakpointsV2}>
              <ImageWithFallback
                src={
                  activeRelease?.coverart ||
                  activeRelease?.thumbnail ||
                  albumCovertArtPlaceholder
                }
                fallback={albumCovertArtPlaceholder}
                alt={releaseTitle || 'Release cover art'}
                className="w-full h-full object-cover rounded" />
            </QuickViewCoverArt>

            <QuickViewContent>
              <QuickViewPillsRow>
                {activeRelease?.markettags?.includes('cancon') && (
                  <QuickViewPill>
                    <MaterialIcon name="CanadaFlag" color={theme.colors.neutralW90} />
                    <span>Canadian content</span>
                  </QuickViewPill>
                )}
                {activeRelease?.is_ai_generated && (
                  <QuickViewPill>
                    <AIFilled width={16} height={16} color={theme.colors.neutralW90} />
                    <span>Contains AI</span>
                  </QuickViewPill>
                )}
              </QuickViewPillsRow>

              <QuickViewTitle
                $responsiveBreakpointsV2={responsiveBreakpointsV2}>
                {releaseTitle}
              </QuickViewTitle>
              {metaItems.length > 0 && (
                <QuickViewMeta>{metaItems.join('  •  ')}</QuickViewMeta>
              )}

              <QuickViewActionsRow>
                <QuickViewPrimaryButton
                  disabled={!activeRelease?.candownload}
                  onClick={() => handleDownloadRelease('wav')}>
                  Download all WAV
                </QuickViewPrimaryButton>
                <QuickViewPrimaryButton
                  disabled={!activeRelease?.candownload}
                  onClick={() => handleDownloadRelease('mp3')}>
                  MP3
                </QuickViewPrimaryButton>
                <QuickViewSecondaryButton onClick={handleViewRelease}>
                  View release
                </QuickViewSecondaryButton>
                <QuickViewReleaseMenu
                  canShareRelease={canShareRelease}
                  disabled={isReleaseMenuDisabled}
                  isArchived={Boolean(activeRelease?.archived)}
                  isFlagged={Boolean(activeRelease?.flagged)}
                  isPlayDisabled={didTapPlay}
                  isPlayLaterDisabled={didTapPlayLater}
                  isPlayNextDisabled={didTapPlayNext}
                  playlists={playlistAbstracts}
                  onAddToPlaylist={handleAddReleaseToPlaylist}
                  onArchive={handleArchiveRelease}
                  onContact={activeRelease?.commenting_enabled ? openContactModal : null}
                  onCreatePlaylist={createNewReleasePlaylist}
                  onFlag={handleFlagRelease}
                  onPlay={handlePlayRelease}
                  onPlayLater={handleReleasePlayLater}
                  onPlayNext={handleReleasePlayNext}
                  onShare={canShareRelease ? openShareReleaseModal : null} />
              </QuickViewActionsRow>
            </QuickViewContent>
          </QuickViewHeader>

          <QuickViewDivider />

          {isDetailLoading && (
            <QuickViewTrackListSkeleton
              responsiveBreakpointsV2={responsiveBreakpointsV2}
              rowCount={estimatedTrackRowCount} />
          )}
          {hasDetailError && <QuickViewErrorState onRetry={fetchReleaseDetail} />}
          {!isDetailLoading && !hasDetailError && (
            <QuickViewTrackList
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
              data-testid="quick-view-track-list">
              {tracks.map((song, index) => (
                <QuickViewTrackRow
                  key={song?.trackid || `${activeRelease?.releaseid}-${index}`}
                  index={index}
                  playlists={playlistAbstracts}
                  responsiveBreakpointsV2={responsiveBreakpointsV2}
                  song={song}
                  onAddToPlaylist={handleAddTrackToPlaylist}
                  onCreatePlaylist={createNewTrackPlaylist}
                  onDownload={handleDownloadSong}
                  onPlay={handlePlaySong}
                  onPlayLater={handleTrackPlayLater}
                  onPlayNext={handleTrackPlayNext} />
              ))}
            </QuickViewTrackList>
          )}
        </QuickViewModalCard>
      </StyledModal>

      <ContactModal
        isContactModalOpen={isContactModalOpen}
        setIsContactModalOpen={setIsContactModalOpen} />
    </>
  )
}

QuickViewModal.propTypes = {
  release: PropTypes.object,
  onClickCloseButton: PropTypes.func
}
